import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../../hooks/toast';
import { useAuth } from '../../../hooks/auth';
import { Screens } from '../../../shared/enums/screens.enum';
import { PermissionActions } from '../../../shared/enums/permissions.enum';
import {
  ATable,
  DropdownOptions,
  MenuOptions,
  PageIntro,
  PageLayout,
  PagePanel,
} from '../../../components';
import {
  PlusCircleOutlined,
  RedoOutlined,
  RocketOutlined,
  SendOutlined,
  InboxOutlined,
  EyeOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { Card, Col, Row, Space, Tag, Typography } from 'antd';
import SendModal from './SendModal';
import {
  CrmMessageDirection,
  CrmMessageLogStatus,
  CrmMessageSource,
  ICrmMessageLog,
  getCrmInboxMessages,
} from '../../../http/crm';

const { Text, Paragraph } = Typography;

const DIRECTION_OPTIONS: { value: CrmMessageDirection; label: string; color: string }[] = [
  { value: 'INBOUND', label: 'Recebida', color: 'blue' },
  { value: 'OUTBOUND', label: 'Enviada', color: 'green' },
];

const SOURCE_OPTIONS: { value: CrmMessageSource; label: string; color: string }[] = [
  { value: 'MANUAL', label: 'Manual', color: 'purple' },
  { value: 'CAMPAIGN', label: 'Campanha', color: 'cyan' },
  { value: 'AUTOMATION', label: 'Automação', color: 'orange' },
];

const STATUS_OPTIONS: { value: CrmMessageLogStatus; label: string; color: string }[] = [
  { value: 'PENDING', label: 'Pendente', color: 'default' },
  { value: 'QUEUED', label: 'Na fila', color: 'processing' },
  { value: 'SENT', label: 'Enviado', color: 'blue' },
  { value: 'DELIVERED', label: 'Entregue', color: 'cyan' },
  { value: 'READ', label: 'Lido', color: 'success' },
  { value: 'FAILED', label: 'Falhou', color: 'error' },
  { value: 'RECEIVED', label: 'Recebido', color: 'geekblue' },
];

const getDirectionLabel = (v: CrmMessageDirection) =>
  DIRECTION_OPTIONS.find((o) => o.value === v)?.label ?? v;
const getDirectionColor = (v: CrmMessageDirection) =>
  DIRECTION_OPTIONS.find((o) => o.value === v)?.color ?? 'default';
const getSourceLabel = (v: CrmMessageSource) =>
  SOURCE_OPTIONS.find((o) => o.value === v)?.label ?? v;
const getSourceColor = (v: CrmMessageSource) =>
  SOURCE_OPTIONS.find((o) => o.value === v)?.color ?? 'default';
const getStatusLabel = (v: CrmMessageLogStatus) =>
  STATUS_OPTIONS.find((o) => o.value === v)?.label ?? v;
const getStatusColor = (v: CrmMessageLogStatus) =>
  STATUS_OPTIONS.find((o) => o.value === v)?.color ?? 'default';

const Messaging: React.FC = () => {
  const { addToast } = useToast();
  const { screenFind, permissionFind } = useAuth();
  const navigate = useNavigate();

  const [search, setSearch] = useState<string>('');
  const [direction, setDirection] = useState<CrmMessageDirection | undefined>(undefined);
  const [source, setSource] = useState<CrmMessageSource | undefined>(undefined);
  const [status, setStatus] = useState<CrmMessageLogStatus | undefined>(undefined);
  const [clientId, setClientId] = useState<string | undefined>(undefined);

  const [messages, setMessages] = useState<ICrmMessageLog[]>([]);
  const [modalSend, setModalSend] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(20);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>();

  const listMessages = useCallback(async () => {
    setLoading(true);
    setMessages([]);
    try {
      const response = await getCrmInboxMessages(
        limit,
        (page - 1) * limit,
        search,
        clientId,
        direction,
        source,
        status,
      );
      setPages(response.count);
      setMessages(response.data);
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: error?.response?.data?.error ?? 'Erro ao listar mensagens.',
      });
    } finally {
      setLoading(false);
    }
  }, [limit, search, page, clientId, direction, source, status, addToast]);

  useEffect(() => {
    listMessages();
  }, [listMessages]);

  return (
    <PageLayout>
      <PageIntro
        badge="CRM"
        icon={<InboxOutlined />}
        title="Inbox de Mensagens CRM"
        description="Histórico de todas as mensagens enviadas e recebidas: manuais, campanhas e automações."
      />
      <MenuOptions
        showInputSearch
        search={search}
        setSearch={setSearch}
        menu_items={[
          {
            label: 'Atualizar',
            key: '1',
            icon: <RedoOutlined />,
            onClick: () => listMessages(),
          },
          permissionFind([PermissionActions.crmMessagingSendManual])
            ? {
                label: 'Nova mensagem manual',
                key: '2',
                icon: <PlusCircleOutlined />,
                onClick: () => setModalSend(true),
              }
            : null,
          ...(screenFind([Screens.crmCampaignsView])
            ? [
                {
                  label: 'Campanhas',
                  key: '3',
                  icon: <RocketOutlined />,
                  onClick: () => navigate('/crm/campaigns'),
                },
              ]
            : []),
          ...(screenFind([Screens.crmTemplatesView])
            ? [
                {
                  label: 'Templates',
                  key: '4',
                  icon: <FileTextOutlined />,
                  onClick: () => navigate('/crm/templates'),
                },
              ]
            : []),
        ]}
      />
      <PagePanel>
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Card title="Direção" size="small" bordered={false} style={{ marginBottom: 12 }}>
              <Space wrap size={[6, 6]}>
                <Tag.CheckableTag
                  checked={!direction}
                  onChange={() => setDirection(undefined)}
                >
                  Todas
                </Tag.CheckableTag>
                {DIRECTION_OPTIONS.map((d) => (
                  <Tag.CheckableTag
                    key={d.value}
                    checked={direction === d.value}
                    onChange={() => setDirection(direction === d.value ? undefined : d.value)}
                  >
                    {d.value === 'INBOUND' ? <ArrowDownOutlined /> : <ArrowUpOutlined />}{' '}
                    {d.label}
                  </Tag.CheckableTag>
                ))}
              </Space>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card title="Fonte / Origem" size="small" bordered={false} style={{ marginBottom: 12 }}>
              <Space wrap size={[6, 6]}>
                <Tag.CheckableTag
                  checked={!source}
                  onChange={() => setSource(undefined)}
                >
                  Todas
                </Tag.CheckableTag>
                {SOURCE_OPTIONS.map((s) => (
                  <Tag.CheckableTag
                    key={s.value}
                    checked={source === s.value}
                    onChange={() => setSource(source === s.value ? undefined : s.value)}
                  >
                    {s.label}
                  </Tag.CheckableTag>
                ))}
              </Space>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card title="Status" size="small" bordered={false} style={{ marginBottom: 12 }}>
              <Space wrap size={[6, 6]}>
                <Tag.CheckableTag
                  checked={!status}
                  onChange={() => setStatus(undefined)}
                >
                  Todos
                </Tag.CheckableTag>
                {STATUS_OPTIONS.map((s) => (
                  <Tag.CheckableTag
                    key={s.value}
                    checked={status === s.value}
                    onChange={() => setStatus(status === s.value ? undefined : s.value)}
                  >
                    {s.label}
                  </Tag.CheckableTag>
                ))}
              </Space>
            </Card>
          </Col>
        </Row>
      </PagePanel>
      <PagePanel>
        <ATable
          scroll={{ y: 510 }}
          columns={[
            {
              title: 'Data',
              dataIndex: 'created_at',
              width: 170,
              render: (value: string) =>
                value ? new Date(value).toLocaleString('pt-BR') : '-',
            },
            {
              title: 'Direção',
              dataIndex: 'direction',
              width: 90,
              render: (value: CrmMessageDirection) => (
                <Tag color={getDirectionColor(value)} icon={
                  value === 'INBOUND' ? <ArrowDownOutlined /> : <ArrowUpOutlined />
                }>
                  {getDirectionLabel(value)}
                </Tag>
              ),
            },
            {
              title: 'Cliente',
              dataIndex: 'client_name',
              width: 200,
              render: (value: string | null | undefined, record: ICrmMessageLog) => (
                <Space direction="vertical" size={2}>
                  <Text strong>{value ?? '— Sem vinculo —'}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {record.recipient_phone}
                  </Text>
                </Space>
              ),
            },
            {
              title: 'Conteúdo',
              dataIndex: 'content',
              render: (value: string | null | undefined) => {
                const text = value ?? '';
                return (
                  <Paragraph
                    ellipsis={{ rows: 2, expandable: false, tooltip: true }}
                    style={{ marginBottom: 0 }}
                  >
                    {text}
                  </Paragraph>
                );
              },
            },
            {
              title: 'Tipo',
              dataIndex: 'source',
              width: 120,
              render: (value: CrmMessageSource) => (
                <Tag color={getSourceColor(value)}>
                  {getSourceLabel(value)}
                </Tag>
              ),
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 120,
              render: (value: CrmMessageLogStatus) => (
                <Tag color={getStatusColor(value)}>
                  {getStatusLabel(value)}
                </Tag>
              ),
            },
            {
              title: 'Opções',
              width: 140,
              render: (_: unknown, record: ICrmMessageLog) => (
                <DropdownOptions
                  items={[
                    {
                      label: 'Ver detalhes',
                      key: '1',
                      icon: <EyeOutlined />,
                      onClick: () => {
                        const details = {
                          id: record.id,
                          phone: record.recipient_phone,
                          content: record.content,
                          source: record.source,
                          direction: record.direction,
                          campaign_id: record.campaign_id,
                        };
                        addToast({
                          type: 'info',
                          title: 'Detalhes da mensagem',
                          description: JSON.stringify(details, null, 2).slice(0, 200),
                        });
                      },
                    },
                    permissionFind([PermissionActions.crmMessagingSendManual])
                      && record.direction === 'INBOUND'
                      ? {
                          label: 'Responder',
                          key: '2',
                          icon: <SendOutlined />,
                          onClick: () => setModalSend(true),
                        }
                      : null,
                  ]}
                />
              ),
            },
          ]}
          loading={loading}
          dataSource={messages}
          page={page}
          setPage={setPage}
          pages={pages}
          limit={limit}
          setLimit={setLimit}
        />
      </PagePanel>
      {modalSend && (
        <SendModal
          open={modalSend}
          close={() => setModalSend(false)}
          onSent={() => listMessages()}
        />
      )}
    </PageLayout>
  );
};

export default Messaging;
